import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
} from '@mui/material';
import { raiseInvoice } from '../../services/esetApi';
import type { EsetLineupLine, EsetRaiseInvoicePeriod, EsetRaiseInvoiceResult } from '../../services/esetApi';

const REPAIRSHOPR_BASE_URL = 'https://techsolvers.repairshopr.com';

/** Minimal identity the dialog needs; periods come from the endpoint on open. */
export interface EsetRaiseInvoiceTarget {
  id: string;
  customerName: string | null;
  productBundle: string | null;
}

interface EsetRaiseInvoiceDialogProps {
  sub: EsetRaiseInvoiceTarget | null; // null = closed
  onClose: () => void;
  /** Called after a REAL run raises an invoice, so the table can refresh. */
  onRaised?: () => void;
}

function fmt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso.slice(0, 10)
    : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' });
}

function money(n: number): string {
  return (n ?? 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

/** True once a REAL run has raised a standalone invoice with a number to link to. */
function isRaisedInvoice(r: EsetRaiseInvoiceResult): boolean {
  return r.status === 'raised' && r.mode !== 'schedule' && r.invoice_number != null;
}

/** The success message for a raised standalone invoice: its number plus deep links to the
 *  RepairShopr invoice and the SM4RT invoice page. */
function raisedInvoiceContent(r: EsetRaiseInvoiceResult): React.ReactNode {
  const number = r.invoice_number;
  return (
    <>
      Done — raised {money(r.total)} (invoice {number}).{' '}
      {r.invoice_id != null && (
        <Link
          href={`${REPAIRSHOPR_BASE_URL}/invoices/${r.invoice_id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View in RepairShopr
        </Link>
      )}
      {r.invoice_id != null && ' · '}
      <Link href={`/invoice/${number}`} target="_blank" rel="noopener noreferrer">
        View in SM4RT
      </Link>
    </>
  );
}

function resultMessage(r: EsetRaiseInvoiceResult): string {
  const n = r.selected_count ?? 0;
  switch (r.status) {
    case 'dry_run':
      return `Dry-run: would raise ${money(r.total)} across ${n} term${n === 1 ? '' : 's'} — nothing written.`;
    case 'raised':
      // The standalone-invoice case renders as JSX (number + links); this covers schedule mode.
      return r.mode === 'schedule'
        ? `Done — added ${money(r.total)} to the customer's monthly invoice schedule.`
        : `Done — raised ${money(r.total)}.`;
    case 'billing_disabled':
      return `ENABLE_BILLING is off — nothing written (would raise ${money(r.total)}).`;
    case 'none_selected':
      return 'No terms selected — nothing to raise.';
    case 'up_to_date':
      return 'Nothing due to bill — all terms are covered.';
    case 'no_start':
      return 'No start date — cannot derive a renewal to bill.';
    case 'no_lineup':
      return `Cannot build the renewal line-up: ${r.error ?? 'a bundle is missing from the catalogue'}.`;
    case 'failed':
      return r.error ? `Failed to raise the invoice: ${r.error}` : 'Failed to raise the invoice.';
    default:
      return `No action: ${r.status}.`;
  }
}

/** Raise a subscription's due renewal term(s): tick the terms to bill, preview with a dry-run,
 *  then confirm the real run (gated by ENABLE_BILLING). Mirrors EsetReconcileDialog. */
export const EsetRaiseInvoiceDialog: React.FC<EsetRaiseInvoiceDialogProps> = ({ sub, onClose, onRaised }) => {
  const [periods, setPeriods] = useState<EsetRaiseInvoicePeriod[]>([]);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [billingEnabled, setBillingEnabled] = useState(true);
  const [emptyStatus, setEmptyStatus] = useState<string | null>(null); // up_to_date | no_start | no_lineup
  const [emptyError, setEmptyError] = useState<string | null>(null);
  const [lineup, setLineup] = useState<{ units?: number; lines?: EsetLineupLine[] }>({});
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false); // initial dry-run fetch
  const [running, setRunning] = useState(false); // preview / confirm action
  const [result, setResult] = useState<EsetRaiseInvoiceResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // On open, dry-run with NO terms to discover the due periods (default all ticked).
  useEffect(() => {
    if (!sub) return;
    let cancelled = false;
    setLoading(true);
    setPeriods([]);
    setChecked({});
    setBillingEnabled(true);
    setEmptyStatus(null);
    setEmptyError(null);
    setLineup({});
    setDryRun(true);
    setResult(null);
    setError(null);
    raiseInvoice(sub.id, { dry_run: true })
      .then((res) => {
        if (cancelled) return;
        const list = res.periods ?? [];
        setPeriods(list);
        setChecked(Object.fromEntries(list.map((p) => [p.start, p.selected !== false])));
        setBillingEnabled(res.billing_enabled !== false);
        setLineup({ units: res.units, lines: res.lineup });
        if (list.length === 0) {
          setEmptyStatus(res.status);
          setEmptyError(res.error ?? null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load due terms');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sub]);

  const selectedStarts = useMemo(
    () => periods.filter((p) => checked[p.start]).map((p) => p.start),
    [periods, checked],
  );
  const selectedTotal = useMemo(
    () => periods.filter((p) => checked[p.start]).reduce((s, p) => s + (p.amount || 0), 0),
    [periods, checked],
  );
  const selectedCount = selectedStarts.length;

  if (!sub) return null;

  const setAll = (on: boolean) => {
    setChecked(Object.fromEntries(periods.map((p) => [p.start, on])));
  };

  const close = () => onClose();

  const execute = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await raiseInvoice(sub.id, { dry_run: dryRun, terms: selectedStarts });
      setResult(res);
      setBillingEnabled(res.billing_enabled !== false);
      if (!dryRun && res.status === 'raised') onRaised?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Raise invoice failed');
    } finally {
      setRunning(false);
    }
  };

  // A real run executes immediately on click (no second confirm step); the warning Alert above
  // the button already spells out what a real run will do.
  const runLabel = dryRun ? 'Preview (dry-run)' : 'Confirm — raise now';

  const isEmpty = !loading && periods.length === 0;
  const busy = loading || running;
  // A REAL run has already raised — lock the action so it can't be billed twice; Close only.
  const alreadyRaised = !!result && result.dry_run === false && result.status === 'raised';
  const canRun = !busy && !alreadyRaised && periods.length > 0 && selectedCount > 0;

  return (
    <Dialog open onClose={busy ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Raise invoice
        <Box sx={{ fontSize: 13, fontWeight: 400, color: '#888', mt: 0.25 }}>
          {sub.customerName || '—'} · {sub.productBundle || '—'}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : isEmpty ? (
          <Box sx={{ fontSize: 13, color: '#888', py: 3, textAlign: 'center' }}>
            {emptyStatus === 'no_start'
              ? 'No start date — cannot derive a renewal to bill.'
              : emptyStatus === 'no_lineup'
                ? `Cannot build the renewal line-up: ${emptyError ?? 'a bundle is missing from the catalogue'}.`
                : 'Nothing due to bill — all terms are covered.'}
          </Box>
        ) : (
          <>
            <Box sx={{ fontSize: 13, color: '#555', mb: 1 }}>
              {periods.length} term{periods.length === 1 ? '' : 's'} due to bill. Tick the term(s) to
              include, preview with a dry-run, then confirm — one invoice with the line-up per term.
            </Box>
            {lineup.lines && lineup.lines.length > 0 && (
              <Box sx={{ fontSize: 13, color: '#555', mb: 1 }}>
                Each term bills <strong>{lineup.units}</strong> licensed unit{lineup.units === 1 ? '' : 's'}:{' '}
                {lineup.lines.map((l) => `${l.quantity}x ${l.sku}`).join(' + ')}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
              <Button size="small" onClick={() => setAll(true)} disabled={running || alreadyRaised}
                sx={{ fontSize: 12, color: '#4A90D9', minWidth: 0 }}>
                Select all
              </Button>
              <Button size="small" onClick={() => setAll(false)} disabled={running || alreadyRaised}
                sx={{ fontSize: 12, color: '#888', minWidth: 0 }}>
                Clear
              </Button>
            </Box>

            <Box sx={{ border: '0.5px solid #e2eaf3', borderRadius: '6px', overflow: 'hidden' }}>
              {periods.map((p) => (
                <Box
                  key={p.start}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5,
                    borderTop: '0.5px solid #eef2f7', '&:first-of-type': { borderTop: 'none' },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={!!checked[p.start]}
                    disabled={running || alreadyRaised}
                    onChange={(e) => setChecked((s) => ({ ...s, [p.start]: e.target.checked }))}
                    sx={{ p: 0.5 }}
                  />
                  <Box sx={{ flex: 1, fontSize: 13, color: '#333' }}>
                    Covers {fmt(p.start)} – {fmt(p.end)}
                  </Box>
                  <Box sx={{ fontSize: 13, color: '#555', fontVariantNumeric: 'tabular-nums' }}>
                    {money(p.amount)}
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 1 }}>
              <Box sx={{ fontSize: 12, color: '#646464' }}>
                {selectedCount} of {periods.length} selected
              </Box>
              <Box sx={{ fontSize: 13, fontWeight: 600, color: '#333', fontVariantNumeric: 'tabular-nums' }}>
                {money(selectedTotal)}
              </Box>
            </Box>

            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Checkbox
                  checked={dryRun}
                  disabled={running || alreadyRaised}
                  onChange={(e) => setDryRun(e.target.checked)}
                />
              }
              label="Dry run (preview, no writes)"
            />

            {!billingEnabled && (
              <Alert severity="warning" sx={{ mt: 0.5 }}>
                ENABLE_BILLING is off — nothing written.
              </Alert>
            )}

            {!dryRun && billingEnabled && selectedCount > 0 && (
              <Alert severity="warning" sx={{ mt: 0.5 }}>
                Real run — this raises {money(selectedTotal)} for {sub.customerName || 'the customer'}{' '}
                across {selectedCount} term{selectedCount === 1 ? '' : 's'} (needs ENABLE_BILLING).
              </Alert>
            )}
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        {result && (
          <Alert
            severity={
              result.status === 'failed'
                ? 'error'
                : result.dry_run
                  ? 'info'
                  : result.status === 'raised'
                    ? 'success'
                    : 'warning'
            }
            sx={{ mt: 1.5 }}
          >
            {isRaisedInvoice(result) ? raisedInvoiceContent(result) : resultMessage(result)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy} sx={{ color: '#aaa' }}>
          Close
        </Button>
        {!isEmpty && (
          <Button
            variant="contained"
            onClick={execute}
            disabled={!canRun}
            color={!dryRun ? 'warning' : 'primary'}
          >
            {runLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
